import { Middleware, MiddlewareAPI, Dispatch, Action } from 'redux';
import UIAction from '../actions/ui/UIAction';
import { AppStates } from '../reducers/app';
import { CurrentTool, LineStyle } from '../reducers/ui/ui';
import * as UIModeAction from '../actions/UIModeAction';
import { UIMode } from '../../../../common/src/ts/types/UIMode';
import { DrawingMode, LineType, ShapeKind, FontType, StampType, Stamp } from '../drawing/types';
import DrawingToolAction from '../drawing/actions/DrawingToolAction';
import ObjectDrawingUIAction from '../actions/ui/ObjectDrawingUIAction';
import FreehandPenUIAction from '../actions/ui/FreehandPenUIAction';
import TextboxUIAction from '../actions/ui/TextboxUIAction';
import { isSelectionAction } from '../actions/SelectionAction';
import { SelectionType } from '../reducers/selection';
import { markerDelete, markerToolSetColorIndex } from '../marker/actions/MarkerAction';
import { MultiroleLinkKind } from '../../../../common/src/ts/types/multiroleLink';
import {
  convertDrawingModeToShapeKind,
  convertColorToColorString,
  convertLineWidthToValue,
  convertFreehandPenWidthToValue,
  isTransparentColor,
  convertLearingPenWidthToValue,
} from '../utils/ui';
import ReflowableContentUIAction from '../actions/ui/ReflowableContentUIAction';
import {
  setFontSize, setFontFamily, FontFamily, setLineHeight, setPadding,
} from '../actions/PreferenceAction';
import {
  fundamentalSizeMultiplier,
  textBoxDefaultFontSize,
  defaultStamps,
} from '../drawing/constants';
import { getBasePath } from '../utils/browser';
import { learningPenColor } from '../constants';

export const uiStateToToolStateMapper: Middleware = ({ getState }: MiddlewareAPI) => (
  next: Dispatch,
) => (action) => {
  next(action);

  const appStates: AppStates = getState();

  if (isSelectionAction(action) && action.selectionState.type === SelectionType.MARKER) {
    if (appStates.ui.currentTool === CurrentTool.MarkerDelete) {
      next(markerDelete(action.selectionState.id));
      return;
    }
  }

  if (!UIAction.isDomesticAction(action)) {
    return;
  }

  switch (action.type) {
    case UIAction.ActionType.SET_CURRENT_TOOL: {
      setUIMode(action.currentTool, next);

      switch (action.currentTool) {
        case CurrentTool.ObjectDrawing: {
          setObjectDrawingState(appStates, next);
          break;
        }
        case CurrentTool.FreeHandPen: {
          setFreehandPenState(appStates, next);
          break;
        }
        case CurrentTool.Handerasing: {
          setHanderasingState(appStates, next);
          break;
        }
        case CurrentTool.Textbox: {
          setTextboxState(appStates, next);
          break;
        }
        case CurrentTool.ImagePasting: {
          next(DrawingToolAction.showObjects(true));
          next(DrawingToolAction.setMode(DrawingMode.ImagePasting));
          break;
        }
        case CurrentTool.LearningPen: {
          setLearningPen(appStates, next);
          break;
        }
        case CurrentTool.ObjectSelection:
        case CurrentTool.ObjectRemoval:
          next(DrawingToolAction.showObjects(true));
        default:
          break;
      }

      break;
    }
    case UIAction.ActionType.SET_MARKER_SELECTED_COLOR_INDEX: {
      // [新] 黒, 青, 緑, 赤, 黄
      // を
      // [旧] 赤, 青, 黄, 緑, 黒
      // へ変換
      const conversionTable = [4, 1, 3, 0, 2];
      next(markerToolSetColorIndex(conversionTable[action.index]));
      break;
    }
    case ObjectDrawingUIAction.ActionType.SELECT_TOOL:
    case ObjectDrawingUIAction.ActionType.SET_SHAPE_KIND:
    case ObjectDrawingUIAction.ActionType.SET_COLOR:
    case ObjectDrawingUIAction.ActionType.SET_LINE_WIDTH:
    case ObjectDrawingUIAction.ActionType.SET_LINE_TYPE:
    case ObjectDrawingUIAction.ActionType.SELECT_STAMP_TYPE: {
      setObjectDrawingState(appStates, next);
      break;
    }
    case FreehandPenUIAction.ActionType.SELECT_TOOL:
    case FreehandPenUIAction.ActionType.SET_PEN_COLOR:
    case FreehandPenUIAction.ActionType.SET_PEN_WIDTH:
    case FreehandPenUIAction.ActionType.SET_LINE_TYPE:
    case FreehandPenUIAction.ActionType.SET_LINE_STYLE: {
      setFreehandPenState(appStates, next);
      break;
    }
    case UIAction.ActionType.SET_HANDERASING_WIDTH: {
      next(DrawingToolAction.showObjects(true));
      next(DrawingToolAction.setLineWidth(action.width));
      break;
    }
    case TextboxUIAction.ActionType.SELECT_TOOL:
    case TextboxUIAction.ActionType.SET_FILL_COLOR:
    case TextboxUIAction.ActionType.SET_LINE_COLOR:
    case TextboxUIAction.ActionType.SET_FONT_TYPE:
    case TextboxUIAction.ActionType.SET_FONT_SIZE:
    case TextboxUIAction.ActionType.SET_LINE_HEIGHT:
    case TextboxUIAction.ActionType.SET_PADDING:
    case TextboxUIAction.ActionType.SET_TEXT_COLOR:
    case TextboxUIAction.ActionType.SET_TOOL_TYPE: {
      setTextboxState(appStates, next);
      break;
    }
    case UIAction.ActionType.SET_MULTIROLE_LINK_STATE: {
      setMultiroleLinkState(appStates, next);
      break;
    }
    case ReflowableContentUIAction.ActionType.SET_FONT_TYPE: {
      switch (action.fontType) {
        case FontType.ContentAware: {
          next(setFontFamily(FontFamily.Textbook));
          break;
        }
        case FontType.Gothic: {
          next(setFontFamily(FontFamily.SansSerif));
          break;
        }
        case FontType.Mincho: {
          next(setFontFamily(FontFamily.Serif));
          break;
        }
      }
      break;
    }
    case ReflowableContentUIAction.ActionType.SET_FONT_SIZE: {
      next(setFontSize(action.fontSize));
      break;
    }
    case ReflowableContentUIAction.ActionType.SET_LINE_HEIGHT: {
      next(setLineHeight(action.lineHeight));
      break;
    }
    case ReflowableContentUIAction.ActionType.SET_PADDING: {
      next(setPadding(action.padding));
      break;
    }
    default:
      break;
  }
};

const setUIMode = (currentTool: CurrentTool, dispatch: Dispatch<Action>) => {
  switch (currentTool) {
    case CurrentTool.ObjectSelection: {
      UIModeAction.setUIMode(UIMode.Drawing, dispatch);
      dispatch(DrawingToolAction.setMode(DrawingMode.Selection));
      break;
    }
    case CurrentTool.ObjectRemoval: {
      UIModeAction.setUIMode(UIMode.Drawing, dispatch);
      dispatch(DrawingToolAction.setMode(DrawingMode.ObjectRemoval));
      break;
    }
    case CurrentTool.Handerasing: {
      UIModeAction.setUIMode(UIMode.Drawing, dispatch);
      dispatch(DrawingToolAction.setMode(DrawingMode.Handerasing));
      break;
    }
    case CurrentTool.ObjectDrawing:
    case CurrentTool.MultiroleLink:
    case CurrentTool.ImagePasting:
    case CurrentTool.Textbox:
    case CurrentTool.FreeHandPen: {
      UIModeAction.setUIMode(UIMode.Drawing, dispatch);
      break;
    }
    case CurrentTool.Touch: {
      UIModeAction.setUIMode(UIMode.Touch, dispatch);
      break;
    }
    case CurrentTool.Mouse: {
      UIModeAction.setUIMode(UIMode.Mouse, dispatch);
      break;
    }
    case CurrentTool.RegionZoom: {
      UIModeAction.setRegionZoomMode(true, dispatch);
      break;
    }
    case CurrentTool.LearningPen:
      UIModeAction.setUIMode(UIMode.Drawing, dispatch);
      break;
    case CurrentTool.MarkerAppend: {
      UIModeAction.setUIMode(UIMode.Marker, dispatch);
      break;
    }
    case CurrentTool.MarkerDelete: {
      UIModeAction.setUIMode(UIMode.Marker, dispatch);
      break;
    }
  }
};

const setLearningPen =  (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showLearningPenObjects(true));

  dispatch(DrawingToolAction.setDrawingAttr(
    convertDrawingModeToShapeKind(ShapeKind.LearningPenRect),
    {
      isTransparent: false,
      isFilled: true,
      lineType: LineType.Solid,
      lineWidth: convertLearingPenWidthToValue(appStates.ui.learningPen.widthIndex),
      lineColor: learningPenColor,
      fillColor: learningPenColor,
    },
  ));
};

const setObjectDrawingState = (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showObjects(true));

  const { objectDrawing } = appStates.ui;
  const { publication } = appStates;
  const toolSetting = objectDrawing.toolSettings[objectDrawing.selected];

  const { shapeKind, color, lineWidth, lineType, selectedStampIndex, stampType } = toolSetting;
  const isFilled = lineType === LineType.None;
  const isTransparent = isTransparentColor(color);
  const colorString = convertColorToColorString(color, 1);

  let stamp: Stamp | null;

  if (stampType === StampType.Default) {
    stamp = { ...defaultStamps[selectedStampIndex],
      path: `${getBasePath()}/${defaultStamps[selectedStampIndex].path}` };
  } else {
    if (!publication || publication === null) {
      throw new Error('Illegal argument.');
    }

    stamp = { ...publication.extraStamps[selectedStampIndex],
      path: `${publication.resourceDir}/${publication.extraStamps[selectedStampIndex].path}` };
  }

  dispatch(DrawingToolAction.setDrawingAttr(
    convertDrawingModeToShapeKind(shapeKind),
    {
      lineType,
      isFilled,
      isTransparent,
      stamp,
      fillColor: colorString,
      lineWidth: convertLineWidthToValue(lineWidth),
      lineColor: isFilled ? 'transparent' : colorString,
    },
  ));
};

const setFreehandPenState = (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showObjects(true));

  const { freehandPen } = appStates.ui;
  const toolSetting = freehandPen.toolSettings[freehandPen.selected];

  const { color, width, lineType, lineStyle } = toolSetting;
  const isTransparent = isTransparentColor(color);
  const colorString = convertColorToColorString(color, 1);
  const lineWidth = convertFreehandPenWidthToValue(width);

  switch (lineStyle) {
    case LineStyle.Freehand: {
      dispatch(DrawingToolAction.setDrawingAttr(
        DrawingMode.Handwriting,
        { lineType, lineWidth, isTransparent, fillColor: colorString, lineColor: colorString },
      ));
      break;
    }
    case LineStyle.HorizontalAxisAligned: {
      dispatch(DrawingToolAction.setDrawingAttr(
        DrawingMode.HorizontalStraightLineHandWriting,
        { lineType, lineWidth, isTransparent, fillColor: colorString, lineColor: colorString },
      ));
      break;
    }
    case LineStyle.VerticalAxisAligned: {
      dispatch(DrawingToolAction.setDrawingAttr(
        DrawingMode.VerticalStraightLineHandWriting,
        { lineType, lineWidth, isTransparent, fillColor: colorString, lineColor: colorString },
      ));
      break;
    }
  }
};

const setTextboxState = (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showObjects(true));

  const { textbox } = appStates.ui;
  const toolSetting = textbox.toolSettings[textbox.selected];

  // FIXME: lineHeight と padding を扱う内部機能がまだ無い
  const {
    fillColor, lineColor, fontType, fontSize, lineHeight, padding, textColor, type,
  } = toolSetting;

  if (type === ShapeKind.None) {
    const isTransparent = isTransparentColor(textColor);

    dispatch(DrawingToolAction.setDrawingAttr(
      convertDrawingModeToShapeKind(ShapeKind.Sticky),
      {
        fontType,
        isTransparent,
        lineHeight,
        padding,
        fontSize: textBoxDefaultFontSize * fontSize / 100 * fundamentalSizeMultiplier,
        fillColor: 'transparent',
        lineColor: 'transparent',
        textColor: convertColorToColorString(textColor, 1),
      },
    ));

    return;
  }

  const isTransparent = isTransparentColor(fillColor);

  dispatch(DrawingToolAction.setDrawingAttr(
    convertDrawingModeToShapeKind(type),
    {
      fontType,
      isTransparent,
      lineHeight,
      padding,
      fontSize: textBoxDefaultFontSize * fontSize / 100 * fundamentalSizeMultiplier,
      fillColor: convertColorToColorString(fillColor, 1),
      lineColor: convertColorToColorString(lineColor, 1),
      textColor: convertColorToColorString(textColor, 1),
    },
  ));
};

const setHanderasingState = (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showObjects(true));

  dispatch(DrawingToolAction.setDrawingAttr(
    DrawingMode.Handerasing,
    { lineWidth: appStates.ui.handerasing.width },
  ));
};

const setMultiroleLinkState = (appStates: AppStates, dispatch: Dispatch<Action>) => {
  dispatch(DrawingToolAction.showObjects(true));
  dispatch(DrawingToolAction.setMode(DrawingMode.MultiroleLinkPasting));

  const kind = appStates.ui.multiroleLink.mode;
  switch (kind) {
    case MultiroleLinkKind.File: {
      const link = appStates.ui.multiroleLink[kind];
      dispatch(DrawingToolAction.setMultiroleLinkToPaste({
        kind,
        file: link.path,
        reference: link.path,
        title: link.title,
        color: link.pinColor,
      }));
      break;
    }
    case MultiroleLinkKind.Web: {
      const link = appStates.ui.multiroleLink[kind];
      dispatch(DrawingToolAction.setMultiroleLinkToPaste({
        kind,
        url: link.url,
        reference: link.url,
        title: link.title,
        color: link.pinColor,
      }));
      break;
    }
    case MultiroleLinkKind.Page: {
      const link = appStates.ui.multiroleLink[kind];
      dispatch(DrawingToolAction.setMultiroleLinkToPaste({
        kind,
        page: link.pageKey,
        reference: link.pageKey,
        title: link.title,
        color: link.pinColor,
      }));
      break;
    }
  }
};
